import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import BusinessHoursFields, {
	defaultBusinessHours,
	parseHolidays,
	readBusinessHours,
} from "./BusinessHoursFields";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
		botEnabled: false,
		ignoreGroups: false,
		outOfHoursMessage: "",
		ratingEnabled: false,
		ratingMessage: "",
		followUpEnabled: false,
		followUpAfterMinutes: 1440,
		followUpMax: 1,
		followUpMessage: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [hours, setHours] = useState(defaultBusinessHours());
	const [holidaysText, setHolidaysText] = useState("");

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp({
					...data,
					outOfHoursMessage: data.outOfHoursMessage || "",
					ratingEnabled: !!data.ratingEnabled,
					ratingMessage: data.ratingMessage || "",
					followUpEnabled: !!data.followUpEnabled,
					followUpAfterMinutes: data.followUpAfterMinutes || 1440,
					followUpMax: data.followUpMax || 1,
					followUpMessage: data.followUpMessage || "",
				});
				const loaded = readBusinessHours(data.businessHours);
				setHours(loaded);
				setHolidaysText((loaded.holidays || []).join("\n"));

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		void fetchSession();
	}, [whatsAppId]);

	const handleSaveWhatsApp = async values => {
		const whatsappData = {
			...values,
			businessHours: { ...hours, holidays: parseHolidays(holidaysText) },
			queueIds: selectedQueueIds,
		};

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setHours(defaultBusinessHours());
		setHolidaysText("");
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							void handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="botEnabled"
												checked={!!values.botEnabled}
											/>
										}
										label={i18n.t("whatsappModal.form.botEnabled")}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="ignoreGroups"
												checked={!!values.ignoreGroups}
											/>
										}
										label={i18n.t("whatsappModal.form.ignoreGroups")}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.farewellMessage")}
										type="farewellMessage"
										multiline
										rows={5}
										fullWidth
										name="farewellMessage"
										error={
											touched.farewellMessage && Boolean(errors.farewellMessage)
										}
										helperText={
											touched.farewellMessage && errors.farewellMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<BusinessHoursFields
									value={hours}
									onChange={setHours}
									holidaysText={holidaysText}
									onHolidaysText={setHolidaysText}
								/>
								{hours.enabled && (
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.hours.message")}
										helperText={i18n.t("whatsappModal.hours.messageHelp", { name: "{{name}}" })}
										multiline
										rows={3}
										fullWidth
										name="outOfHoursMessage"
										variant="outlined"
										margin="dense"
									/>
								)}
								<FormControlLabel
									control={
										<Field
											as={Switch}
											color="primary"
											name="ratingEnabled"
											checked={!!values.ratingEnabled}
										/>
									}
									label={i18n.t("quality.ratingEnabled")}
								/>
								{values.ratingEnabled && (
									<Field
										as={TextField}
										label={i18n.t("quality.ratingMessage")}
										helperText={i18n.t("quality.ratingHelp", {
											protocol: "{{protocol}}",
											name: "{{name}}",
										})}
										multiline
										rows={3}
										fullWidth
										name="ratingMessage"
										variant="outlined"
										margin="dense"
									/>
								)}
								<FormControlLabel
									control={
										<Field
											as={Switch}
											color="primary"
											name="followUpEnabled"
											checked={!!values.followUpEnabled}
										/>
									}
									label={i18n.t("schedule.followUpEnabled")}
								/>
								{values.followUpEnabled && (
									<>
										<div style={{ display: "flex", gap: 8 }}>
											<Field
												as={TextField}
												type="number"
												label={i18n.t("schedule.followUpAfter")}
												name="followUpAfterMinutes"
												variant="outlined"
												margin="dense"
												inputProps={{ min: 30, max: 20160 }}
												style={{ flex: 1 }}
											/>
											<Field
												as={TextField}
												type="number"
												label={i18n.t("schedule.followUpMax")}
												name="followUpMax"
												variant="outlined"
												margin="dense"
												inputProps={{ min: 1, max: 3 }}
												style={{ flex: 1 }}
											/>
										</div>
										<Field
											as={TextField}
											label={i18n.t("schedule.followUpMessage")}
											helperText={i18n.t("schedule.followUpHelp", { name: "{{name}}" })}
											multiline
											rows={3}
											fullWidth
											name="followUpMessage"
											variant="outlined"
											margin="dense"
										/>
									</>
								)}
								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
